import type { MedVisitCitation } from "@/services/medChronServiceV2.types";

import type { MedVisit } from "../../../types/medchron";

export type VisitCitationClickHandler = (citation: MedVisitCitation, visitId?: string) => void;

/** Visit type extended with collapsed-therapy display fields */
export interface VisitDisplay extends MedVisit {
  isCollapsed?: boolean;
  visitCount?: number;
  startDate?: string | null;
  endDate?: string | null;
  allVisits?: MedVisit[];
  /** Current name of the version this visit was copied from; unset when not a copy or that version is gone. */
  sourceVersionName?: string;
}

/** Minimal template shape for visit types and detail categories */
export interface MedChronTemplateVisitType {
  name: string;
  details?: Record<string, { name: string }>;
  collapsible?: boolean;
}

export interface MedChronTemplate {
  visitTypes?: Record<string, MedChronTemplateVisitType>;
}

export interface VisitTypeFilterOption {
  readonly visitTypeId: string;
  readonly label: string;
  readonly visitCount: number;
}

/** Editing state for a single visit detail */
export interface EditingVisitDetailState {
  visitId: string;
  category: string;
  newCategory: string;
  details: string;
}

/** Pages stay strings so partial input survives until validation. */
export interface CitationDraftRow {
  key: string;
  file_id: string;
  start_page: string;
  end_page: string;
}

export type CitationRowErrors = Partial<Record<"file_id" | "start_page" | "end_page", string>>;

export interface CitationDraftValidation {
  isValid: boolean;
  citations: MedVisitCitation[];
  /** Index-aligned with the input rows; one entry per row, empty when valid. */
  rowErrors: CitationRowErrors[];
}

/** Header form values when editing a visit */
export interface EditingHeaderValues {
  visit_date: string;
  visit_type_id: string;
  provider_name: string;
  facility_name: string;
  visit_subtype: string;
  citations: CitationDraftRow[];
}

/** New visit form values */
export interface NewVisitValues {
  visit_date: string;
  visit_type_id: string;
  provider_name: string;
  facility_name: string;
  visit_subtype: string;
  citations: CitationDraftRow[];
}

/** Case file from supabase for file selector */
export interface CaseFileOption {
  file_id: string;
  bucket_url: string;
  filename?: string | null;
  page_count?: number | null;
}

export enum ChronologyViewType {
  // TODO: need to remane date to none.
  DATE = "dateOnly",
  PROVIDER = "provider",
  VISIT_TYPE = "visitType",
  FILE = "file",
}

export enum ChronologySortField {
  DATE = "date",
  PAGE = "page",
  NAME = "name",
}

export enum SortDirection {
  ASC = "asc",
  DESC = "desc",
}

/** One row in the virtualized visits list (group header or visit card). */
export type VisitsFlatItem =
  | { kind: "header"; groupKey: string; groupLabel: string; visitCount: number }
  | { kind: "visit"; visit: VisitDisplay };

/** Delete confirm target for detail */
export interface DeleteDetailConfirm {
  versionId: string;
  visitId: string;
  category: string;
}

export interface VisitFieldSuggestions {
  providers: readonly string[];
  facilities: readonly string[];
  subtypes: readonly string[];
}
